import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react';
import { llmUsageApi, LlmUsageDetailResponse } from '../services/api';
import './LlmUsageDetailPage.css';

const formatJson = (text: string): string => {
    try {
        return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
        return text;
    }
};

const LlmUsageDetailPage: React.FC = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<LlmUsageDetailResponse | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const run = async () => {
            if (!id) {
                setError('パラメータが不正です');
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                setError(null);
                const res = await llmUsageApi.getById(id);
                setData(res);
            } catch (e: any) {
                const status = e?.status;
                if (status === 401 || status === 403) {
                    setError('権限がありません。ログイン状態を確認してください。');
                } else if (status === 404) {
                    setError('このLLM呼び出しレコードは見つかりません。');
                } else {
                    setError(e?.message || 'LLM使用状況の詳細を取得できませんでした');
                }
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [id]);

    const item = data?.item;

    return (
        <div className="llm-usage-detail-page">
            <div className="traceback-header">
                <Link to="/llm-usage" className="back-link">
                    <ArrowLeft size={16} />
                    一覧に戻る
                </Link>
                <h2 className="traceback-title">LLM呼び出し詳細</h2>
            </div>

            {loading && (
                <div className="traceback-loading">
                    <Loader2 size={20} className="animate-spin" />
                    <span>読み込み中...</span>
                </div>
            )}

            {!loading && error && (
                <div className="traceback-error">
                    <AlertTriangle size={18} />
                    <div>
                        <div className="traceback-error-title">取得に失敗しました</div>
                        <div className="traceback-error-message">{error}</div>
                    </div>
                </div>
            )}

            {!loading && !error && item && (
                <div className="traceback-card">
                    <div className="traceback-meta llm-usage-detail-meta">
                        <div className="meta-row">
                            <span className="meta-label">作成日時</span>
                            <span className="meta-value">{new Date(item.created_at).toLocaleString('ja-JP')}</span>
                        </div>
                        <div className="meta-row">
                            <span className="meta-label">Job ID</span>
                            <span className="meta-value">{item.job_id || '—'}</span>
                        </div>
                        <div className="meta-row">
                            <span className="meta-label">URL</span>
                            <span className="meta-value">{item.url || '—'}</span>
                        </div>
                        <div className="meta-row">
                            <span className="meta-label">ガイドライン</span>
                            <span className="meta-value">{item.guideline_id || '—'}</span>
                        </div>
                        <div className="meta-row">
                            <span className="meta-label">ルール種別</span>
                            <span className="meta-value">{item.rule_type || '—'} ({item.violation_category_id || '—'})</span>
                        </div>
                        <div className="meta-row">
                            <span className="meta-label">モデル</span>
                            <span className="meta-value">{item.model}</span>
                        </div>
                        <div className="meta-row">
                            <span className="meta-label">トークン</span>
                            <span className="meta-value">
                                入力 {item.prompt_tokens.toLocaleString()} / 出力 {item.completion_tokens.toLocaleString()} /
                                {' '}キャッシュ {item.cached_tokens.toLocaleString()} / 推論 {item.reasoning_tokens.toLocaleString()} /
                                {' '}合計 {item.total_tokens.toLocaleString()}
                            </span>
                        </div>
                        {(item.html_tokens != null || item.rule_prompt_tokens != null) && (
                            <div className="meta-row">
                                <span className="meta-label">入力内訳(推定)</span>
                                <span className="meta-value">
                                    HTML {item.html_tokens != null ? item.html_tokens.toLocaleString() : '—'}
                                    {item.rule_prompt_tokens != null && (
                                        <> / ガイドラインプロンプト {item.rule_prompt_tokens.toLocaleString()}</>
                                    )}
                                </span>
                            </div>
                        )}
                        {item.cost != null && (
                            <div className="meta-row">
                                <span className="meta-label">コスト</span>
                                <span className="meta-value">${item.cost.toFixed(4)}</span>
                            </div>
                        )}
                    </div>

                    <div className="traceback-section">
                        <div className="traceback-section-title">送信プロンプト</div>
                        <pre className="traceback-pre">{data?.prompt_text || 'プロンプトを取得できませんでした'}</pre>
                    </div>

                    <div className="traceback-section">
                        <div className="traceback-section-title">レスポンス（整形済みJSON）</div>
                        <pre className="traceback-pre">
                            {data?.response_text ? formatJson(data.response_text) : 'レスポンスを取得できませんでした'}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LlmUsageDetailPage;
